/**
 * ShopProductsBlock, Editorial Commerce
 *
 * A curated, contextual product row embedded in an editorial article. Reads a
 * group heading (block.title), an optional one-line intro (block.intro), and an
 * ordered array of product UUIDs (block.product_ids). Fetches those products
 * from the live `products` table in ONE query, preserves the editor's chosen
 * order, and renders editorial affiliate cards wired to REAL data.
 *
 * Outbound retailer links are the business model: they open in a new tab with
 * rel="sponsored nofollow noopener" (correct affiliate SEO). This is distinct
 * from the editorial "no outbound links in prose" rule.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient.js';
import { trackExternalClick } from '../../services/outboundClickService.js';
import { imgUrl } from '../../utils/imgUrl.js';

const FU   = "'Nunito', 'Inter', 'Helvetica Neue', sans-serif";
const FD   = "'Gilda Display', 'Playfair Display', Georgia, serif";
const GOLD = '#c9a96e';

function formatPrice(price, currency = 'GBP') {
  if (price === null || price === undefined || price === '') return '';
  const n = Number(price);
  if (Number.isNaN(n)) return '';
  const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';
  return `${sym}${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ── Save (bookmark) button ─────────────────────────────────────────────────
function SaveButton({ saved, onToggle, isLight }) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPressed(true); setTimeout(() => setPressed(false), 200); onToggle(); }}
      aria-label={saved ? 'Remove from saved' : 'Save product'}
      style={{
        position: 'absolute', top: 10, right: 10,
        width: 30, height: 30, borderRadius: '50%',
        background: 'rgba(255,255,255,0.9)', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)', zIndex: 2,
        transform: pressed ? 'scale(0.88)' : 'scale(1)',
        transition: 'transform 0.15s ease',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill={saved ? GOLD : 'none'}
        stroke={saved ? GOLD : 'rgba(30,28,22,0.55)'} strokeWidth="1.8" style={{ display: 'block' }}>
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    </button>
  );
}

// ── Single editorial product card (real data via props) ─────────────────────
function ShopCard({ product, isLight, inRail = false }) {
  const [hovered, setHovered] = useState(false);
  const [saved, setSaved] = useState(false);

  const BG     = isLight ? '#fff' : '#111';
  const BORDER = isLight ? 'rgba(30,28,22,0.1)' : 'rgba(245,240,232,0.1)';
  const TEXT   = isLight ? '#1a1806' : '#f5f0e8';
  const MUTED  = isLight ? 'rgba(30,28,22,0.5)' : 'rgba(245,240,232,0.5)';

  const brand    = product.brand_name || product.retailer_name || '';
  const currency = product.currency || 'GBP';
  const hasSale  = product.sale_price && Number(product.sale_price) < Number(product.price);
  const url      = product.affiliate_url || product.product_url || '';
  const retailer = product.retailer_name || '';

  // Strip a leading "BRAND - " prefix from the name for a cleaner title.
  let title = product.name || '';
  for (const v of [brand.toUpperCase(), brand]) {
    if (v && title.startsWith(v + ' - ')) { title = title.slice(v.length + 3); break; }
  }

  const onOutbound = (e) => {
    e.stopPropagation();
    if (url) trackExternalClick({ entityType: 'magazine', entityId: null, venueId: null, linkType: 'brochure', url });
  };

  const linkRel = 'sponsored nofollow noopener';

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: BG, border: `1px solid ${hovered ? GOLD + '50' : BORDER}`,
        borderRadius: 2, overflow: 'hidden',
        transition: 'border-color 0.25s, box-shadow 0.25s',
        boxShadow: hovered ? `0 8px 32px ${GOLD}15` : 'none',
        display: 'flex', flexDirection: 'column',
        // In a horizontal rail each card is a consistent, fixed width and never
        // shrinks, the rail scrolls instead of squashing cards.
        ...(inRail ? { flex: '0 0 auto', width: 'clamp(190px, 62vw, 240px)', scrollSnapAlign: 'start' } : {}),
      }}
    >
      {/* Image */}
      <a
        href={url || '#'} target="_blank" rel={linkRel} onClick={onOutbound}
        style={{ display: 'block', overflow: 'hidden', position: 'relative', aspectRatio: '4/5', background: isLight ? '#f6f3ee' : '#161616' }}
      >
        <img src={imgUrl(product.image_url, { width: 600, resize: 'contain' })} alt={title} loading="lazy" style={{
          width: '100%', height: '100%', objectFit: 'cover', display: 'block',
          transform: hovered ? 'scale(1.06)' : 'scale(1)', transition: 'transform 0.7s ease',
        }} onError={(e) => { e.currentTarget.style.opacity = 0; }} />
        <SaveButton saved={saved} onToggle={() => setSaved(s => !s)} isLight={isLight} />
      </a>

      {/* Content */}
      <div style={{ padding: '18px 16px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontFamily: FU, fontSize: 8, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD, marginBottom: 7 }}>
          {brand}
        </div>
        <div style={{ fontFamily: FD, fontSize: 'clamp(15px, 1.4vw, 18px)', fontWeight: 400, color: TEXT, lineHeight: 1.25, marginBottom: 12, flex: 1 }}>
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, marginTop: 'auto' }}>
          <span style={{ fontFamily: FU, fontSize: 14, fontWeight: 600, color: hasSale ? '#c9704e' : TEXT }}>
            {formatPrice(hasSale ? product.sale_price : product.price, currency)}
          </span>
          {hasSale && (
            <span style={{ fontFamily: FU, fontSize: 12, color: MUTED, textDecoration: 'line-through' }}>
              {formatPrice(product.price, currency)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <a
            href={url || '#'} target="_blank" rel={linkRel} onClick={onOutbound}
            style={{
              fontFamily: FU, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
              color: '#fff', background: '#000',
              padding: '9px 16px', borderRadius: 1, textDecoration: 'none', display: 'inline-block',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 0.85}
            onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
          >
            {retailer ? `View at ${retailer}` : 'View'}
          </a>
        </div>
      </div>
    </article>
  );
}

// ── Block ───────────────────────────────────────────────────────────────────
export default function ShopProductsBlock({ block, C }) {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded]     = useState(false);
  const ids = Array.isArray(block.product_ids) ? block.product_ids.filter(Boolean) : [];

  const isLight = C && typeof C.bg === 'string'
    ? /^#(f|e|d)/i.test(C.bg.trim()) || C.bg === '#fff' || C.bg === '#ffffff'
    : true;

  useEffect(() => {
    let alive = true;
    if (!ids.length) { setLoaded(true); return; }
    (async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, brand_name, retailer_name, price, sale_price, currency, image_url, affiliate_url, product_url')
        .in('id', ids)
        .eq('active', true);
      if (!alive) return;
      if (error || !data) { setProducts([]); setLoaded(true); return; }
      // Preserve editor's chosen order; skip products with no image.
      const byId = new Map(data.map(p => [p.id, p]));
      const ordered = ids.map(id => byId.get(id)).filter(p => p && p.image_url);
      setProducts(ordered);
      setLoaded(true);
    })();
    return () => { alive = false; };
  }, [ids.join(',')]);

  if (loaded && products.length === 0) return null;

  const TEXT  = C?.text  || '#1a1806';
  const MUTED = C?.muted || (isLight ? 'rgba(30,28,22,0.55)' : 'rgba(245,240,232,0.55)');
  const BORDER = isLight ? 'rgba(30,28,22,0.18)' : 'rgba(245,240,232,0.22)';
  const BORDER_FAINT = isLight ? 'rgba(30,28,22,0.08)' : 'rgba(245,240,232,0.1)';

  return (
    <div style={{ margin: 'clamp(64px, 9vw, 104px) auto', maxWidth: 1080, padding: '0 clamp(20px,4vw,56px)', boxSizing: 'border-box', width: '100%' }}>
      <ShopRail
        title={block.title}
        intro={block.intro}
        products={products}
        isLight={isLight}
        TEXT={TEXT} MUTED={MUTED} BORDER={BORDER} BORDER_FAINT={BORDER_FAINT}
      />
    </div>
  );
}

// ── Horizontal carousel rail (reuses the ShopCarousel scroll pattern) ────────
function ShopRail({ title, intro, products, isLight, TEXT, MUTED, BORDER, BORDER_FAINT }) {
  const trackRef = useRef(null);
  const [canBack, setCanBack] = useState(false);
  const [canFwd,  setCanFwd]  = useState(true);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const checkScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanBack(el.scrollLeft > 4);
    setCanFwd(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll, products]);

  const scrollByDir = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  const ArrowBtn = ({ dir, disabled }) => (
    <button
      onClick={() => scrollByDir(dir)}
      disabled={disabled}
      aria-label={dir < 0 ? 'Scroll back' : 'Scroll forward'}
      style={{
        width: isMobile ? 34 : 30, height: isMobile ? 34 : 30,
        border: `1px solid ${disabled ? BORDER_FAINT : BORDER}`,
        background: 'none', cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: disabled ? BORDER_FAINT : MUTED, fontSize: 16,
        borderRadius: 1, transition: 'all 0.18s', flexShrink: 0,
        WebkitTapHighlightColor: 'transparent',
      }}
    >{dir < 0 ? '‹' : '›'}</button>
  );

  return (
    <div>
      {/* Header row: title/intro + arrows */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
        <div style={{ minWidth: 0 }}>
          {title && (
            <h3 style={{ fontFamily: FD, fontWeight: 400, fontSize: 'clamp(19px, 2vw, 26px)', color: TEXT, margin: '0 0 6px', lineHeight: 1.2 }}>
              {title}
            </h3>
          )}
          {intro && (
            <p style={{ fontFamily: FU, fontSize: 13, fontWeight: 300, color: MUTED, margin: 0, lineHeight: 1.6 }}>
              {intro}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <ArrowBtn dir={-1} disabled={!canBack} />
          <ArrowBtn dir={1}  disabled={!canFwd} />
        </div>
      </div>

      {/* Scrollable track, cards scroll horizontally within the article column */}
      <div
        ref={trackRef}
        style={{
          display: 'flex', gap: 'clamp(14px, 1.6vw, 22px)', overflowX: 'auto',
          scrollSnapType: 'x mandatory', scrollbarWidth: 'none',
          paddingBottom: 4, WebkitOverflowScrolling: 'touch',
        }}
      >
        {products.map(p => <ShopCard key={p.id} product={p} isLight={isLight} inRail />)}
      </div>
    </div>
  );
}
